import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import { HistoryPanelDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { usePanelData } from 'modules/finance/components/FinancialWorkspace/components/ResponsivePageWithPanel/dataProvider'
import {
  PERIODS_LABELS,
  getDatePeriod,
} from 'modules/finance/components/FinancialWorkspace/lib/datePeriod'
import { TableDataVariables } from 'modules/finance/components/FinancialWorkspace/types/table'
import { FullTimeEquivalent } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/components/HistoryPanel/FullTimeEquivalent'
import { useWorkingTimePercentages } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/hooks/useWorkingTimePercentages'
import { getFilteredVariables } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/lib/filteredVariables'

import { CollapsableButton } from './CollapsableButton'
import { Joiners } from './CollapsableItemDetails/Joiners'
import { Leavers } from './CollapsableItemDetails/Leavers'
import { Promotions } from './CollapsableItemDetails/Promotions'
import { Content } from './Content'
import { Headcount } from './Headcount'
import './index.styles.css'
export interface PanelData {
  label: string
  endDate: string
  startDate: string
  chartLabel?: string
}

interface Props {
  fteEnabled: boolean
  formattedState: TableDataVariables
}

export const HistoryPanel = ({ fteEnabled, formattedState }: Props) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { navigation } = useNavigation()
  const { data: panelData } = usePanelData<PanelData>()
  const [showResumed, setShowResumed] = useState(true)
  const [showResumedButton, setShowResumedButton] = useState(false)

  const monthsBetweenDates = useUnsafeMemo(() => {
    if (!panelData?.startDate || !panelData?.endDate) return []

    const start = moment(panelData.startDate).startOf('month')
    const end = moment(panelData.endDate).startOf('month')
    const months: string[] = []

    const current = start.clone()

    while (current.isSameOrBefore(end)) {
      months.push(current.format('YYYY-MM'))
      current.add(1, 'month')
    }
    return months.reverse()
  }, [panelData.startDate, panelData.endDate])

  const filteredVariables = useUnsafeMemo(
    () =>
      Object.fromEntries(
        Object.entries(
          getFilteredVariables({ formattedState, customCategoryIds: [], agreementDataTypes: [] })
        ).filter(([_, value]) => value && value.length > 0)
      ),
    [formattedState]
  )

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const variables = useUnsafeMemo(() => {
    return {
      companyId,
      startDate: moment(panelData.startDate).startOf('month').format('YYYY-MM-DD'),
      endDate: moment(panelData.endDate).endOf('month').format('YYYY-MM-DD'),
      ...filteredVariables,
    }
  }, [panelData.startDate, panelData.endDate, companyId, filteredVariables])

  const [fetch, { data: queryData, loading }] = useLazyQuery(HistoryPanelDocument, {
    variables,
  })

  const contractsData = queryData?.contracts
  const joiners = contractsData?.joinersConnection?.nodes
  const leavers = contractsData?.leaversConnection?.nodes
  const promotions = contractsData?.promotionsDatesConnection?.nodes

  const {
    averageHeadcount,
    hasMissingWorkingTimePercentage,
    loading: loadingWorkingTimePercentages,
  } = useWorkingTimePercentages({
    fteEnabled,
    employeeIds: contractsData?.monthlyHeadcountsConnection?.nodes[0]?.employeeIds,
  })

  useUnsafeEffect(() => {
    const period = getDatePeriod(moment(panelData.startDate), moment(panelData.endDate))

    if (period === PERIODS_LABELS.monthly) {
      setShowResumedButton(false)
      setShowResumed(true)
    } else {
      setShowResumedButton(true)
      setShowResumed(false)
    }
  }, [panelData.startDate, panelData.endDate])

  useUnsafeEffect(() => {
    fetch()
  }, [variables])

  const isFtePage =
    pathname === navigation.finance.financialWorkspace.employee_costs.import_fte.fullPath()

  return (
    <Box height="full">
      {fteEnabled && hasMissingWorkingTimePercentage && (
        <Box paddingTop="s16">
          <F0Alert
            title={i18n.t('finance.financial_workspace.employee_costs.workforce.alert.title')}
            description={i18n.t(
              'finance.financial_workspace.employee_costs.workforce.alert.description'
            )}
            variant="warning"
            action={{
              label: i18n.t('finance.financial_workspace.employee_costs.workforce.alert.button'),
              disabled: isFtePage,
              onClick: () => {
                navigate(
                  navigation.finance.financialWorkspace.employee_costs.import_fte.fullPath(),
                  {
                    state: {
                      employeeIds:
                        contractsData?.monthlyHeadcountsConnection?.nodes[0]?.employeeIds ?? [],
                    },
                  }
                )
              },
            }}
          />
        </Box>
      )}
      <Box paddingY="s16">
        <Text.Normal size="200" weight="bold" capitalize>
          {panelData.label}
        </Text.Normal>
      </Box>

      <Box flexDirection="row" justifyContent="spaceBetween">
        <Headcount
          loading={loading}
          count={contractsData?.monthlyHeadcountsConnection?.nodes[0]?.headcount ?? 0}
        />
        {fteEnabled && (
          <FullTimeEquivalent
            count={averageHeadcount}
            loading={loading || loadingWorkingTimePercentages}
            hasMissingWorkingTimePercentage={hasMissingWorkingTimePercentage}
          />
        )}
      </Box>

      <Box overflowY="auto" paddingBottom="s32" className={'HistoryPanel_container'}>
        {showResumed && (
          <Content
            title={i18n.t('finance.financial_workspace.employee_costs.workforce.summary')}
            loading={loading}
            data={{
              joiners: {
                count: joiners?.reduce((acc, node) => acc + node.joiners, 0) ?? 0,
                details: (
                  <Joiners
                    employeeIds={joiners?.flatMap((node) => node.employeeIds) ?? []}
                    startDate={panelData.startDate}
                    endDate={panelData.endDate}
                  />
                ),
              },
              leavers: {
                count: leavers?.reduce((acc, node) => acc + node.leavers, 0) ?? 0,
                details: (
                  <Leavers
                    employeeIds={leavers?.flatMap((node) => node.employeeIds) ?? []}
                    startDate={panelData.startDate}
                    endDate={panelData.endDate}
                  />
                ),
              },
              promotions: {
                count: promotions?.reduce((acc, node) => acc + node.approved, 0) ?? 0,
                details: (
                  <Promotions
                    contractVersionIds={
                      promotions?.flatMap((node) => node.approvedContractVersionsIds) ?? []
                    }
                  />
                ),
              },
            }}
          />
        )}

        {!showResumed &&
          monthsBetweenDates.map((month, index) => {
            const reverseIndex = monthsBetweenDates.length - 1 - index

            const detailedJoiners = joiners?.[reverseIndex]
            const detailedLeavers = leavers?.[reverseIndex]
            const detailedPromotions = promotions?.find((node) =>
              moment(node.date, 'YYYY-MM').isSame(moment(month), 'month')
            )

            const params = {
              startDate: moment(month).startOf('month').format('YYYY-MM-DD'),
              endDate: moment(month).endOf('month').format('YYYY-MM-DD'),
            }

            return (
              <Content
                key={month}
                title={moment(month).format('MMMM')}
                loading={loading}
                data={{
                  joiners: {
                    count: detailedJoiners?.joiners ?? 0,
                    details: (
                      <Joiners employeeIds={detailedJoiners?.employeeIds ?? []} {...params} />
                    ),
                  },
                  leavers: {
                    count: detailedLeavers?.leavers ?? 0,
                    details: (
                      <Leavers employeeIds={detailedLeavers?.employeeIds ?? []} {...params} />
                    ),
                  },
                  promotions: {
                    count: detailedPromotions?.approved ?? 0,
                    details: (
                      <Promotions
                        contractVersionIds={detailedPromotions?.approvedContractVersionsIds ?? []}
                      />
                    ),
                  },
                }}
              />
            )
          })}
      </Box>

      {showResumedButton && !loading && (
        <Box alignItems="center">
          <CollapsableButton
            showResumed={showResumed}
            onClick={() => setShowResumed(!showResumed)}
          />
        </Box>
      )}
    </Box>
  )
}
